/**
 * LLM Intelligence Service
 *
 * Shared narration helper for the intelligence agents (Fracture, Compass,
 * Keystone). The agents do their *detection* deterministically in TypeScript —
 * the maths is exact and auditable. The LLM is used only to turn a structured
 * detection into a crisp, executive-readable narrative + recommended action.
 *
 * This separation matters: the finding's existence and evidence never depend on
 * a model being available. If no LLM is reachable, each analyzer falls back to a
 * deterministic template, so the agent still produces real, trustworthy output.
 */

import type { AgentCard } from '../../../supabase/functions/_shared/instructionStackCore.ts';
import { buildFindingsSystemPrompt, findingsAgentCard } from '../../../supabase/functions/_shared/findingsCore.ts';
import { callLlmWithFallback } from '../llmClient';

export interface NarrationRequest {
  /** Agent persona, e.g. 'Fracture, a decision red-team analyst'. */
  persona: string;
  /** What kind of finding this is, in plain words. */
  findingKind: string;
  /** Structured evidence the model must explain — never invent beyond this. */
  evidence: Record<string, unknown>;
  /** Optional workspace-author override prompt. */
  /** The workspace's prompt overlay — it shapes the wording, never replaces the contract. */
  systemPrompt?: string | null;
  model?: string;
  /** The agent's card for the instruction stack; a generic one when absent. */
  card?: AgentCard;
  /** agent_settings.model_params: temperature, max tokens and language inside the narration bound. */
  modelParams?: { temperature: number; maxTokens: number; language: string | null } | null;
}

const GENERIC_CARD: AgentCard = findingsAgentCard('intelligence', 'Explain a finding the detector already computed.', 'Help a leader see why a computed finding matters and what to do about it.');

function systemFor(req: NarrationRequest): string {
  return buildFindingsSystemPrompt({ card: req.card ?? GENERIC_CARD, overlay: req.systemPrompt ?? null, language: req.modelParams?.language ?? null });
}

export interface Narration {
  /** One-line headline (<= ~120 chars). */
  summary: string;
  /** Two to four sentences: what it means + the recommended next move. */
  detail: string;
}


function buildPrompt(req: NarrationRequest): string {
  return `You are ${req.persona}.

You have detected a "${req.findingKind}" finding. Here is the exact evidence (already computed — treat as ground truth):

${JSON.stringify(req.evidence, null, 2)}

Write:
- "summary": a single punchy sentence a leader would stop and read. Lead with the surprising fact.
- "detail": 2-4 sentences. Explain why it matters for the decision at hand, then state the specific recommended action. Reference the concrete numbers and item names from the evidence. No preamble, no hedging.

Respond with JSON only.`;
}

function parse(content: string): Narration | null {
  try {
    const match = content.match(/\{[\s\S]*\}/);
    if (!match) return null;
    const obj = JSON.parse(match[0]) as Record<string, unknown>;
    const summary = typeof obj.summary === 'string' ? obj.summary.trim() : '';
    const detail = typeof obj.detail === 'string' ? obj.detail.trim() : '';
    if (!summary && !detail) return null;
    return { summary, detail };
  } catch {
    return null;
  }
}

/**
 * Narrate a finding. Returns null when no LLM is reachable or parsing fails —
 * callers should fall back to their own deterministic copy.
 */
export async function narrateFinding(req: NarrationRequest): Promise<Narration | null> {
  try {
    const content = await callLlmWithFallback({
      prompt: buildPrompt(req),
      conversationId: `intel-${Date.now()}`,
      systemPrompt: systemFor(req),
      model: req.model,
    });
    if (!content) return null;
    return parse(content);
  } catch {
    return null;
  }
}
